// Refine — the "where does my time actually pay off" page.
//
// Reads the classified errors the evaluate hook persists (attempt_errors),
// scopes them to the learner's current level, and ranks the weak areas so they
// know what to practise next. Deliberately diagnostic, not a scoreboard: no
// streaks, no badges — just "spend your next session here; you've got that one."
import { isAuthed } from "../lib/auth.js";
import { fetchAiProfile, fetchErrorStats, fetchAiAttempts } from "../lib/ai.js";
import { openAuthPanel } from "../ui/authPanel.js";
import { navigate } from "../router/router.js";

const WINDOW_DAYS = 30;
const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];

// The five coarse domains, with how they read in the UI. Order here is the
// fallback display order before counts re-sort them weakest-first.
const DOMAINS = [
  { id: "sentence_structure", label: "Sentence structure", emoji: "🧩", blurb: "Word order, adjective & pronoun placement, prepositions." },
  { id: "conjugation", label: "Conjugation", emoji: "🔀", blurb: "Verb forms, tense and mood." },
  { id: "agreement", label: "Agreement", emoji: "⚖️", blurb: "Gender and number on articles, nouns, adjectives." },
  { id: "vocabulary", label: "Vocabulary", emoji: "📖", blurb: "Word choice and false friends." },
  { id: "orthography", label: "Spelling & accents", emoji: "✍️", blurb: "Accent marks and spelling slips." },
];
const DOMAIN_BY_ID = Object.fromEntries(DOMAINS.map((d) => [d.id, d]));

const TYPE_LABEL = {
  lexical: "word choice",
  false_friend: "false friends",
  conjugation: "verb forms",
  mood_tense: "tense / mood",
  agreement: "gender / number",
  word_order: "word order",
  adjective_placement: "adjective placement",
  pronoun_placement: "pronoun placement",
  preposition: "prepositions",
  omission: "omissions",
  accent_spelling: "accents / spelling",
};

export async function refineView(app) {
  if (!isAuthed()) {
    app.innerHTML = `
      <section class="max-w-xl mx-auto py-12 text-center">
        <p class="text-4xl mb-3">🛠️</p>
        <h2 class="text-2xl font-bold text-sage-800">Training Overview</h2>
        <p class="text-sage-600 text-sm mt-2 mb-5">A diagnostic view of where your Portuguese needs work — built from your AI Translate attempts. Sign in to see your weak spots.</p>
        <button id="rf-signin" class="bg-sage-500 hover:bg-sage-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium">Sign in</button>
      </section>`;
    app.querySelector("#rf-signin").addEventListener("click", () => openAuthPanel("signin"));
    return;
  }

  app.innerHTML = `<div class="py-16 text-center text-sage-400 text-sm">Reading your practice history…</div>`;

  // Default to the level you're actually practising — the tier of your most
  // recent attempt — not the slow rolling ai_level estimate (which lags at A1).
  const [profile, latest] = await Promise.all([fetchAiProfile(), fetchAiAttempts(1)]);
  const recentTier = latest && latest[0] && latest[0].tier;
  let level = recentTier || (profile && (profile.current_learning_tier || profile.ai_level)) || "A2";
  if (LEVELS.indexOf(level) < 0) level = "A2";
  const starterDone = !!(profile && profile.a1_starter_done);

  app.innerHTML = `
    <section class="max-w-2xl mx-auto fade-in">
      ${headerHTML(level, profile && profile.ai_level)}
      <div id="rf-body"></div>
    </section>`;

  const sel = app.querySelector("#rf-level");
  if (sel) sel.addEventListener("change", () => renderBody(app, sel.value, starterDone));
  await renderBody(app, level, starterDone);
  window.scrollTo({ top: 0, behavior: "smooth" });
}

// The A1 beginner on-ramp shown on the Training Overview: the First Words module
// (until completed) and an AI vocabulary card — somewhere to start before any
// sentence practice exists.
function a1OnboardHTML(starterDone) {
  return `
    <div class="grid gap-3 sm:grid-cols-2 mt-5">
      ${
        !starterDone
          ? `<button data-go-starter class="text-left bg-gradient-to-b from-sage-50 to-white border border-sage-200 rounded-2xl p-4 hover:border-sage-300 transition">
               <p class="text-2xl">🚀</p>
               <p class="font-semibold text-sage-800 mt-1">Start here · First words</p>
               <p class="text-xs text-sage-500 mt-1">36 essential phrases to learn on day one.</p>
             </button>`
          : ""
      }
      <button data-go-vocab class="text-left bg-gradient-to-b from-sage-50 to-white border border-sage-200 rounded-2xl p-4 hover:border-sage-300 transition">
        <p class="text-2xl">✨</p>
        <p class="font-semibold text-sage-800 mt-1">AI vocabulary</p>
        <p class="text-xs text-sage-500 mt-1">Pick a theme — restaurant, travel — and build a deck that comes back for review.</p>
      </button>
    </div>
    <div class="mt-4">
      <p class="text-xs font-semibold uppercase tracking-wide text-sage-400 mb-2">Quick guides — open any time</p>
      <div class="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <button data-go-classes class="paper sketch-box-sm p-3 text-left hover:shadow-md transition">
          <p class="text-xl">🧱</p><p class="font-script text-xl text-sage-800 leading-none">Word Classes</p>
          <p class="text-xs text-sage-500" style="font-family:Inter,sans-serif">The building blocks of grammar</p>
        </button>
        <button data-go-sound class="paper sketch-box-sm p-3 text-left hover:shadow-md transition">
          <p class="text-xl">🔊</p><p class="font-script text-xl text-sage-800 leading-none">Sound Map</p>
          <p class="text-xs text-sage-500" style="font-family:Inter,sans-serif">How the letters sound</p>
        </button>
        <button data-go-core class="paper sketch-box-sm p-3 text-left hover:shadow-md transition">
          <p class="text-xl">🗺️</p><p class="font-script text-xl text-sage-800 leading-none">Core 100 Words</p>
          <p class="text-xs text-sage-500" style="font-family:Inter,sans-serif">The words you'll meet most</p>
        </button>
        <button data-go-word class="paper sketch-box-sm p-3 text-left hover:shadow-md transition">
          <p class="text-xl">🧩</p><p class="font-script text-xl text-sage-800 leading-none">Word Order</p>
          <p class="text-xs text-sage-500" style="font-family:Inter,sans-serif">How a sentence is built</p>
        </button>
      </div>
    </div>`;
}

function wireA1Onboard(root) {
  const s = root.querySelector("[data-go-starter]");
  if (s) s.addEventListener("click", () => navigate("article", { slug: "a1-first-words" }));
  const v = root.querySelector("[data-go-vocab]");
  if (v) v.addEventListener("click", () => navigate("flashcards"));
  const map = { "[data-go-classes]": "wordclasses", "[data-go-sound]": "soundmap", "[data-go-core]": "core100", "[data-go-word]": "wordorder" };
  for (const sel in map) {
    const el = root.querySelector(sel);
    if (el) el.addEventListener("click", () => navigate(map[sel]));
  }
}

// Fetch and render everything that depends on the chosen level, into #rf-body.
// Called on first load and whenever the level selector changes.
async function renderBody(app, level, starterDone) {
  const body = app.querySelector("#rf-body");
  if (!body) return;
  body.innerHTML = `<div class="py-12 text-center text-sage-400 text-sm">Reading your ${level} practice…</div>`;

  const stats = await fetchErrorStats({ days: WINDOW_DAYS, tier: level });

  // No data yet. For A1 beginners, show the on-ramp (First Words + AI vocab)
  // instead of only nudging them at sentence practice.
  if (!stats.attempts) {
    if (level === "A1") {
      body.innerHTML = `
        <div class="mt-5">
          <p class="text-sage-700 font-medium">New here? Two easy ways to begin.</p>
          ${a1OnboardHTML(starterDone)}
          <p class="text-xs text-sage-400 mt-4">When you're ready for full sentences, try <button data-go-ai class="underline text-sage-600 hover:text-sage-800">AI Translate</button> — your weak spots will then show up on this page.</p>
        </div>`;
      wireA1Onboard(body);
      const goAi = body.querySelector("[data-go-ai]");
      if (goAi) goAi.addEventListener("click", () => navigate("ai-trainer"));
      return;
    }
    body.innerHTML = `
      <div class="bg-white border border-dashed border-sage-200 rounded-2xl p-8 text-center mt-5">
        <div class="text-4xl mb-3">🌱</div>
        <p class="text-sage-700 font-medium">No practice at ${level} yet</p>
        <p class="text-sage-500 text-sm mt-1 mb-4">Do a few AI Translate exercises at this level and this page will show exactly where your time is best spent.</p>
        <button id="rf-go" class="bg-sage-500 hover:bg-sage-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium">Start AI Translate</button>
      </div>`;
    body.querySelector("#rf-go").addEventListener("click", () => navigate("ai-trainer"));
    return;
  }

  // Rank the five domains weakest-first; split into "focus" (has errors) and
  // "solid" (none in the window). Solid only counts as mastery once there's
  // enough practice behind it.
  const ranked = DOMAINS.map((d) => ({ ...d, count: stats.byDomain[d.id] || 0 })).sort((a, b) => b.count - a.count);
  const focus = ranked.filter((d) => d.count > 0);
  const solid = ranked.filter((d) => d.count === 0);
  const enoughData = stats.attempts >= 5;
  const top = focus[0] || null;
  const nonCorrect = (stats.partial || 0) + (stats.incorrect || 0);
  const hasBreakdown = stats.totalErrors > 0;
  // Attempts that weren't fully correct yet carry no classified errors — almost
  // always because they were graded before detailed error-tracking was enabled.
  // We must NOT read that as "no errors / mastered".
  const noDetail = !hasBreakdown && nonCorrect > 0;
  const canClaimMastery = hasBreakdown || nonCorrect === 0;

  const topTypesFor = (domainId) => {
    const t = Object.entries(stats.byType)
      .filter(([type]) => DOMAIN_BY_ID[domainId] && domainFor(type) === domainId)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([type, n]) => `${TYPE_LABEL[type] || type} (${n})`);
    return t.join(" · ");
  };

  const heroHTML = top
    ? `<div class="bg-gradient-to-b from-sage-50 to-white border border-sage-200 rounded-2xl p-5 mt-5">
         <p class="text-xs uppercase tracking-wide text-sage-500 font-semibold mb-1">Spend your next session here</p>
         <div class="flex items-start gap-3">
           <span class="text-3xl leading-none">${top.emoji}</span>
           <div class="min-w-0 flex-1">
             <h3 class="text-xl font-bold text-sage-800">${top.label}</h3>
             <p class="text-sage-600 text-sm">${top.blurb}</p>
             <p class="text-sage-500 text-xs mt-1">${top.count} ${top.count === 1 ? "slip" : "slips"} in the last ${stats.windowDays} days${topTypesFor(top.id) ? " — mostly " + topTypesFor(top.id) : ""}.</p>
           </div>
           <button data-train="${top.id}" class="shrink-0 bg-sage-500 hover:bg-sage-600 text-white px-4 py-2 rounded-lg text-sm font-medium">${top.id === "vocabulary" ? "Review words" : "Train this"}</button>
         </div>
       </div>`
    : noDetail
      ? `<div class="bg-gradient-to-b from-sage-50 to-white border border-sage-200 rounded-2xl p-5 mt-5">
           <p class="text-xs uppercase tracking-wide text-sage-500 font-semibold mb-1">No breakdown yet</p>
           <p class="text-sage-700 text-sm">${nonCorrect} of your ${level} attempts weren't fully correct, but they were graded before detailed error-tracking was switched on — so there's nothing to break down here yet. New attempts will start filling in your weak areas.</p>
           <button data-train-any class="mt-3 bg-sage-500 hover:bg-sage-600 text-white px-4 py-2 rounded-lg text-sm font-medium">Start a session</button>
         </div>`
      : `<div class="bg-gradient-to-b from-sage-50 to-white border border-sage-200 rounded-2xl p-5 mt-5 text-center">
           <p class="text-2xl mb-1">🎉</p>
           <p class="text-sage-700 font-medium">No errors at ${level} in the last ${stats.windowDays} days.</p>
           <p class="text-sage-500 text-sm mt-1">Either you're cruising — or it's time to bump up a level.</p>
           <button data-train-any class="mt-3 bg-sage-500 hover:bg-sage-600 text-white px-4 py-2 rounded-lg text-sm font-medium">Practice anyway</button>
         </div>`;

  const focusCards = focus
    .map((d) => {
      const types = topTypesFor(d.id);
      return `
        <div class="bg-white border border-sage-100 rounded-xl p-4 flex items-center gap-3">
          <span class="text-2xl leading-none">${d.emoji}</span>
          <div class="min-w-0 flex-1">
            <div class="flex items-center justify-between gap-2">
              <h4 class="font-semibold text-sage-800">${d.label}</h4>
              <span class="text-xs text-sage-400 tabular-nums">${d.count} ${d.count === 1 ? "slip" : "slips"}</span>
            </div>
            ${types ? `<p class="text-xs text-sage-500 mt-0.5">${types}</p>` : ""}
          </div>
          <button data-train="${d.id}" class="shrink-0 text-xs font-medium px-3 py-1.5 rounded-full border border-sage-200 text-sage-600 hover:border-sage-400">${d.id === "vocabulary" ? "Review" : "Train"}</button>
        </div>`;
    })
    .join("");

  const solidHTML =
    enoughData && solid.length && canClaimMastery
      ? `<div class="mt-6">
           <h3 class="text-sm font-semibold text-sage-500 uppercase tracking-wide mb-2">Looking solid</h3>
           <div class="flex flex-wrap gap-2">
             ${solid
               .map(
                 (d) => `<span class="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700">${d.emoji} ${d.label}</span>`
               )
               .join("")}
           </div>
           <p class="text-xs text-sage-400 mt-2">No slips here in the last ${stats.windowDays} days — no need to keep drilling these.</p>
         </div>`
      : "";

  body.innerHTML = `
    ${statsHTML(stats, level)}
    ${level === "A1" && !starterDone ? a1OnboardHTML(starterDone) : ""}
    ${heroHTML}
    ${focus.length ? `<div class="space-y-2 mt-5">${focusCards}</div>` : ""}
    ${solidHTML}
    <p class="text-xs text-sage-400 mt-6">Counts cover the last ${stats.windowDays} days at ${level}. This is a guide, not a grade.</p>`;
  wireA1Onboard(body);

  // Vocabulary slips train via your "My words" deck; everything else drills in a
  // targeted AI Translate session built around that weakness.
  body.querySelectorAll("[data-train]").forEach((b) =>
    b.addEventListener("click", () => {
      const d = b.dataset.train;
      if (d === "vocabulary") navigate("flashcards", { deck: "personal" });
      else navigate("ai-trainer", { focus: d });
    })
  );
  // General "just start practising" button (shown when there's no specific weak area).
  const trainAny = body.querySelector("[data-train-any]");
  if (trainAny) trainAny.addEventListener("click", () => navigate("ai-trainer"));
}

// The training-volume + accuracy strip, scoped to the chosen level. Accuracy is
// correct ÷ attempts; the bar splits attempts into correct / almost / off.
function pct(n, total) {
  return total ? Math.round((n / total) * 100) : 0;
}
function statsHTML(stats, level) {
  return `
    <div class="bg-white border border-sage-100 rounded-2xl p-4 mt-4">
      <div class="flex items-center justify-between gap-4">
        <div>
          <p class="text-2xl font-bold text-sage-800 tabular-nums">${stats.accuracy}%</p>
          <p class="text-xs text-sage-500">correct at ${level}</p>
        </div>
        <div class="text-right">
          <p class="text-2xl font-bold text-sage-800 tabular-nums">${stats.attempts}</p>
          <p class="text-xs text-sage-500">${stats.attempts === 1 ? "attempt" : "attempts"} · ${stats.windowDays} days</p>
        </div>
      </div>
      <div class="mt-3 flex h-2 rounded-full overflow-hidden bg-sage-50">
        <div class="bg-sage-400" style="width:${pct(stats.correct, stats.attempts)}%"></div>
        <div class="bg-amber-300" style="width:${pct(stats.partial, stats.attempts)}%"></div>
        <div class="bg-rose-300" style="width:${pct(stats.incorrect, stats.attempts)}%"></div>
      </div>
      <div class="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-sage-500">
        <span><span class="inline-block w-2 h-2 rounded-full bg-sage-400 align-middle"></span> ${stats.correct} correct</span>
        <span><span class="inline-block w-2 h-2 rounded-full bg-amber-300 align-middle"></span> ${stats.partial} almost</span>
        <span><span class="inline-block w-2 h-2 rounded-full bg-rose-300 align-middle"></span> ${stats.incorrect} off</span>
      </div>
    </div>`;
}

function headerHTML(level, estLevel) {
  const opts = LEVELS.map((l) => `<option value="${l}"${l === level ? " selected" : ""}>${l}</option>`).join("");
  const est = estLevel
    ? `<p class="text-xs mt-1.5"><span class="inline-block px-2 py-0.5 rounded-full bg-sage-100 text-sage-700 font-semibold">Estimated level ${estLevel}</span> <span class="text-sage-400">— a rough read from your recent scores, to suggest where to train</span></p>`
    : "";
  return `
    <div class="flex items-start justify-between gap-3">
      <div>
        <h2 class="text-2xl font-bold text-sage-800">Training Overview</h2>
        <p class="text-sage-600 text-sm mt-1">Where your time pays off most — pick the level you're working on.</p>
        ${est}
      </div>
      <label class="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-sage-500">Level
        <select id="rf-level" class="mt-1 block border border-sage-200 rounded-lg px-2 py-1.5 text-sm font-normal normal-case bg-white text-sage-800">${opts}</select>
      </label>
    </div>`;
}

// Map a fine type back to its domain so the per-type breakdown can be filtered
// per card. Mirrors the server-side TYPE_DOMAIN map in pb_hooks/ai.pb.js.
function domainFor(type) {
  switch (type) {
    case "lexical":
    case "false_friend":
      return "vocabulary";
    case "conjugation":
    case "mood_tense":
      return "conjugation";
    case "agreement":
      return "agreement";
    case "word_order":
    case "adjective_placement":
    case "pronoun_placement":
    case "preposition":
    case "omission":
      return "sentence_structure";
    case "accent_spelling":
      return "orthography";
    default:
      return "other";
  }
}
